import {
  BrandConfigProvider,
  initApiClient,
  GATrackingProvider,
  createBrandGTMLoader,
} from '@checkout/core'
import { ThemeProvider } from '@checkout/themes'
import { ToastProvider, useToast, ToastContainer } from '@checkout/ui'
import { useEffect, useState } from 'react'

import { createCheckoutRequestHeadersResolver } from '../api/request-context'
import { brandConfig } from '../config'

interface AppProvidersProps {
  children: React.ReactNode
}

/** Toast 容器组件，使用 ToastContext */
function ToastContainerWrapper() {
  const { toasts, hideToast } = useToast()
  return <ToastContainer toasts={toasts} onClose={hideToast} />
}

// 默认 analytics 配置（如果品牌未配置）
const defaultAnalyticsConfig = {
  enabled: false,
  gtmId: undefined,
  modulePrefix: '',
}

const appVersion =
  typeof import.meta.env.VITE_APP_VERSION === 'string' &&
  import.meta.env.VITE_APP_VERSION.trim() !== ''
    ? import.meta.env.VITE_APP_VERSION.trim()
    : undefined

export function AppProviders({ children }: AppProvidersProps) {
  const [isReady, setIsReady] = useState(false)

  // Initialize API client and GTM on mount
  useEffect(() => {
    // 初始化 API 客户端
    initApiClient({
      baseURL: brandConfig.api.baseURL,
      timeout: brandConfig.api.timeout,
      requestContext: {
        productCode: brandConfig.api.productCode,
        clientVersion: appVersion,
        resolveAdditionalHeaders: createCheckoutRequestHeadersResolver(
          brandConfig.api.productCode,
          appVersion
        ),
      },
    })

    // 加载 GTM（仅在生产环境且配置了 analytics 时）
    const analyticsConfig = brandConfig.analytics ?? defaultAnalyticsConfig
    if (analyticsConfig.enabled && analyticsConfig.gtmId) {
      const loadGTM = createBrandGTMLoader(analyticsConfig)
      loadGTM()
    }

    setIsReady(true)
  }, [])

  // Don't render children until API client is initialized
  if (!isReady) {
    return null
  }

  const analyticsConfig = brandConfig.analytics ?? defaultAnalyticsConfig

  return (
    <BrandConfigProvider config={brandConfig}>
      <ThemeProvider brand={brandConfig}>
        <GATrackingProvider config={analyticsConfig}>
          <ToastProvider>
            {children}
            <ToastContainerWrapper />
          </ToastProvider>
        </GATrackingProvider>
      </ThemeProvider>
    </BrandConfigProvider>
  )
}
